/**
 * The site's own furniture, in each language.
 *
 * Kept as a plain object rather than i18next: this app has no API, no user and no runtime config,
 * so loading a translation framework and fetching JSON at runtime would add a network round-trip and
 * a loading state to a static page whose entire content is already in the bundle. `check-i18n`
 * recognises this app as "resources are inline" and skips it by design.
 *
 * Every string the reader can see belongs here. A hardcoded English word in a component is the bug
 * this file exists to prevent — the same one that shipped English to Romanian clinics elsewhere.
 */
import { persistSharedLang, type CookieBannerLabels } from '@easydent/shared-ui';
import { AVAILABLE_LANGS } from '@/content';

export interface UiStrings {
  brand: string;
  searchPlaceholder: string;
  searchEmpty: (q: string) => string;
  homeTitle: string;
  homeIntro: string;
  next: string;
  back: string;
  notFoundTitle: string;
  notFoundBody: string;
  notFoundLink: string;
  fallbackNotice: string;
  footer: string;
  themeLabel: string;
  themeLight: string;
  themeDark: string;
  themeSystem: string;
  footerHelp: string;
  navLabel: string;
  languageLabel: string;
  loading: string;
  loadFailed: string;
  /**
   * Cookie-banner text, handed to the shared component as-is.
   *
   * Nested rather than flattened into ten more fields here because the shape is the shared
   * component's, not this app's — keeping it typed as `CookieBannerLabels` means adding a label
   * there is a compile error here rather than a silently English string on a Romanian page.
   */
  cookieBanner: CookieBannerLabels;
}

export const UI: Record<string, UiStrings> = {
  en: {
    brand: 'help',
    searchPlaceholder: 'Search the guides…',
    searchEmpty: (q) => `Nothing matches “${q}”.`,
    homeTitle: 'easyDent help',
    homeIntro:
      'Short, task-shaped guides for the things you do every day. Each one is a job to be done, not a tour of a menu.',
    next: 'Next',
    back: '← Back to easyDent',
    notFoundTitle: 'Page not found',
    notFoundBody: 'That guide does not exist.',
    notFoundLink: 'Start from the beginning',
    fallbackNotice: 'This page has not been translated yet, so it is shown in English.',
    footer: 'Can’t find it? Open',
    footerHelp: 'inside easyDent and raise a ticket — it reaches the same people who wrote these pages.',
    navLabel: 'Guides',
    languageLabel: 'Language',
    loading: 'Loading the guides…',
    loadFailed: 'The guides could not be loaded. Refresh to try again.',
    themeLabel: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    cookieBanner: {
      dialogLabel: 'Cookie consent',
      intro: 'We use strictly necessary cookies to make this work. With your agreement we also use analytics and marketing cookies.',
      policyLink: 'Cookie policy',
      necessary: 'Strictly necessary (always on)',
      analytics: 'Analytics',
      marketing: 'Marketing',
      acceptAll: 'Accept all',
      necessaryOnly: 'Necessary only',
      customise: 'Customise',
      saveChoice: 'Save choice',
    },
  },
  ro: {
    brand: 'ajutor',
    searchPlaceholder: 'Caută în ghiduri…',
    searchEmpty: (q) => `Nimic nu se potrivește cu „${q}”.`,
    homeTitle: 'Ajutor easyDent',
    homeIntro:
      'Ghiduri scurte, organizate pe activități, pentru lucrurile pe care le faci zilnic. Fiecare răspunde la o sarcină concretă, nu descrie un meniu.',
    next: 'Următorul',
    back: '← Înapoi la easyDent',
    notFoundTitle: 'Pagina nu a fost găsită',
    notFoundBody: 'Acest ghid nu există.',
    notFoundLink: 'Începe de la început',
    fallbackNotice: 'Această pagină nu este încă tradusă, așa că este afișată în engleză.',
    footer: 'Nu găsești ce cauți? Deschide',
    footerHelp:
      'în easyDent și trimite un tichet — ajunge la aceiași oameni care au scris aceste pagini.',
    navLabel: 'Ghiduri',
    languageLabel: 'Limbă',
    loading: 'Se încarcă ghidurile…',
    loadFailed: 'Ghidurile nu au putut fi încărcate. Reîmprospătează pagina.',
    themeLabel: 'Temă',
    themeLight: 'Luminoasă',
    themeDark: 'Întunecată',
    themeSystem: 'Sistem',
    cookieBanner: {
      dialogLabel: 'Consimțământ cookie-uri',
      intro: 'Folosim cookie-uri strict necesare pentru funcționare. Cu acordul tău, folosim și cookie-uri de analiză și de marketing.',
      policyLink: 'Politica de cookie-uri',
      necessary: 'Strict necesare (mereu active)',
      analytics: 'Analiză',
      marketing: 'Marketing',
      acceptAll: 'Accept toate',
      necessaryOnly: 'Doar necesare',
      customise: 'Personalizează',
      saveChoice: 'Salvează alegerea',
    },
  },
};

/**
 * Display names, keyed by code. Only languages that actually have a content folder are offered —
 * so a name added here before the guides exist does not put a dead option in the switcher, and a
 * folder added without a name still appears, under its code, rather than disappearing.
 */
const LANGUAGE_NAMES: { code: string; label: string }[] = [
  { code: 'ro', label: 'Română' },
  { code: 'en', label: 'English' },
];

/**
 * Same list, same order, same labels as the clinic app's header menu — a reader moving between the
 * two should not notice they have changed application. Languages with no content folder are dropped
 * so the menu can never offer a guide set that does not exist; a folder with no label still shows,
 * under its code, rather than disappearing.
 */
export function languageOptions(): { code: string; label: string }[] {
  const named = LANGUAGE_NAMES.filter((l) => AVAILABLE_LANGS.includes(l.code));
  const extra = AVAILABLE_LANGS.filter((c) => !LANGUAGE_NAMES.some((l) => l.code === c))
    .map((code) => ({ code, label: code.toUpperCase() }));
  return [...named, ...extra];
}

/**
 * Persist the choice through the shared helper, not a local copy.
 *
 * `persistSharedLang` writes the `edlang` cookie on the parent domain *and* the two localStorage
 * keys the other apps read, so choosing Romanian here is still Romanian in the clinic app. An
 * earlier version of this file rolled its own cookie with a different domain rule, which is exactly
 * how two apps end up disagreeing about the same preference.
 */
export function setLang(code: string): void {
  persistSharedLang(code);
  const url = new URL(window.location.href);
  url.searchParams.set('lang', code);
  window.location.assign(url.toString());
}
